/**
 * Level 4 — one contract, decluttered into five tabs so the page stops being a
 * wall of data: Snapshot · Documents · Invoices · Alerts · Cognara.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Loader2, Sparkles, FolderTree, ReceiptText, BellRing, LayoutGrid, Move } from 'lucide-react';
import api from '@/lib/api';
import { Panel, fmtINR, fmtDate, RiskPill, StatusPill } from './portfolioUi';
import { DocumentFolders } from './DocumentFolders';
import { InvoiceLinkagePanel } from './InvoiceLinkagePanel';
import { ContractAlertsPanel } from './ContractAlertsPanel';
import { CognaraContractChat } from './CognaraContractChat';
import { HeaderExtractCard } from './HeaderExtractCard';
import { MoveVendorDialog } from './ContractDialogs';

const TABS = [
  ['snapshot', 'Snapshot', LayoutGrid],
  ['documents', 'Documents', FolderTree],
  ['invoices', 'Invoices', ReceiptText],
  ['alerts', 'Alerts & milestones', BellRing],
  ['cognara', 'Cognara', Sparkles],
];

const Fact = ({ label, value, testId }) => (
  <div className="py-3 border-b last:border-b-0" style={{ borderColor: 'var(--border-subtle)' }} data-testid={testId}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: 'var(--text-muted)' }}>{label}</p>
    <p className="text-sm mt-1" style={{ color: 'var(--text-primary)' }}>{value ?? '—'}</p>
  </div>
);

export const LevelContract = ({ summary, projectId, vendors = [], onChanged }) => {
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('snapshot');
  const [moveOpen, setMoveOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/contracts/contracts/${summary.id}`);
      setContract(data);
    } catch { /* summary still renders */ }
    finally { setLoading(false); }
  }, [summary.id]);

  useEffect(() => { load(); }, [load]);

  const fin = contract?.financials || {};
  const dates = contract?.dates || {};
  const ai = contract?.ai_analysis || {};
  const value = fin.current_value ?? fin.original_value ?? summary.value;

  return (
    <div className="space-y-6" data-testid="level-contract">
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
                  className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs transition-colors"
                  style={{ borderColor: tab === key ? 'var(--brand-accent)' : 'var(--border-color)',
                           background: tab === key ? 'var(--active-bg)' : 'transparent',
                           color: tab === key ? 'var(--text-primary)' : 'var(--text-secondary)' }}
                  data-testid={`contract-tab-${key}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-2">
          <button onClick={() => setMoveOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs"
                  style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                  data-testid="contract-move-vendor">
            <Move className="w-3.5 h-3.5" /> Move vendor
          </button>
          <button onClick={() => navigate(`/contracts/${summary.id}`)}
                  className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs"
                  style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="open-full-contract">
            Full workspace <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </span>
      </div>

      {tab === 'snapshot' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Panel className="lg:col-span-7" title={summary.contract_number || 'Contract'} hint={summary.title} testId="contract-facts-panel">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <StatusPill status={summary.status} />
              <RiskPill score={summary.risk_score} testId="contract-risk-pill" />
              {ai.clause_count != null && (
                <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                  <Sparkles className="w-3 h-3" /> {ai.clause_count} clauses · {ai.obligation_count ?? 0} obligations
                </span>
              )}
            </div>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--brand-accent)' }} /></div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <Fact label="Contract value" value={value ? fmtINR(value) : 'Not set'} testId="fact-value" />
                <Fact label="Currency" value={fin.currency || summary.currency} />
                <Fact label="Contract type" value={summary.type} />
                <Fact label="Vendor" value={contract?.parties?.contractor || contract?.vendor_name || 'Unassigned'} />
                <Fact label="Start" value={fmtDate(dates.commencement_date || dates.start_date || summary.start_date)} />
                <Fact label="Completion" value={fmtDate(dates.completion_date || dates.end_date || summary.end_date)} />
                <Fact label="LD exposure" value={fmtINR(summary.ld_exposure)} />
                <Fact label="Claims / variations / disputes" value={`${summary.claims} · ${summary.variations} · ${summary.disputes}`} />
              </div>
            )}
          </Panel>
          <div className="lg:col-span-5 space-y-6">
            <Panel title="Cognara header extraction" hint="Read supplier, value and dates off the uploaded PO" testId="extract-panel">
              <HeaderExtractCard contractId={summary.id} onApplied={() => { load(); onChanged?.(); }} />
            </Panel>
          </div>
        </div>
      )}

      {tab === 'documents' && (
        <DocumentFolders contractId={summary.id} onChanged={() => { load(); onChanged?.(); }} />
      )}

      {tab === 'invoices' && (
        <InvoiceLinkagePanel contractId={summary.id} contractNumber={summary.contract_number} onChanged={onChanged} />
      )}

      {tab === 'alerts' && <ContractAlertsPanel contractId={summary.id} />}

      {tab === 'cognara' && (
        <CognaraContractChat contractId={summary.id} contractNumber={summary.contract_number} />
      )}

      <MoveVendorDialog
        open={moveOpen} onOpenChange={setMoveOpen}
        contract={summary} vendors={vendors} projectId={projectId}
        onMoved={() => onChanged?.()}
      />
    </div>
  );
};

export default LevelContract;
